import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { clone as cloneSkinned } from 'three/examples/jsm/utils/SkeletonUtils.js';
import { applyKitPaint, type KitPaint } from './kitRecolor';
import {
  CLIP_TIERS,
  DOWN_CLIPS,
  ONE_SHOT_CLIPS,
  resolveAvailableClip,
  idleStartOffset,
  idleTimeScale,
  type PlayerClipKind,
} from './playerClips';

// Skinned-mesh players (D21, ops-session visual upgrade): replaces the dummy pills with the Meshy
// biped, plus the Soccer Game Pack clips retargeted onto it by tools/retarget-anim (D107).
// Presentation-only — every input comes from ViewEntity (§13.5); the sim is untouched. The pool loads
// async; scene.ts keeps rendering pills until `load()` resolves and falls back to pills permanently
// if the tier-1 assets are missing.
//
// D107 tiering: only CLIP_TIERS[0] is awaited. Later tiers stream in behind first render and a rig
// binds them lazily, so an unloaded clip costs a fallback rather than a stall. See playerClips.ts.

// ── clip selection lives in playerClips.ts (pure — the tested core) ──
// Re-exported so callers keep importing the renderer's animation API from one place.
export {
  pickClip,
  isDownAction,
  SPRINT_ANIM_MIN_SPEED,
  RUN_ANIM_MIN_SPEED,
  type PlayerClipKind,
  type ClipInput,
} from './playerClips';

// ── playback tuning (presentation-only constants) ──
const MODEL_HEIGHT_M = 1.8; // the sim capsule (CAPSULE_H) — meshes are normalized to this
// D68: recolor everything from the feet up to this fraction of the bind-pose height; above it (the chin
// and the whole head) keeps its baked texture so the face/eyes/teeth are never repainted. Tuned for the
// chibi proportions (large head): the chin sits ~62 % up. Lower ⇒ cuts into the collar; higher ⇒ the eyes leak.
const RECOLOR_CHIN_FRACTION = 0.62;
const FADE_LOCO_S = 0.18;
const FADE_ONESHOT_S = 0.06;
const FADE_ONESHOT_OUT_S = 0.22;
const KICK_TIME_SCALE = 2.2; // the 1.67 s clip compressed to FAFI's snappy ~0.75 s kick
const SLIDE_TIME_SCALE = 1.4; // 1.57 s clip ≈ 1.1 s slide+recovery
const PUNCH_TIME_SCALE = 2.5; // D46 Item 8 / D67: the 4 s boxing clip compressed 2.5× (~1.6 s). The fist reaches
// full extension at raw ~1.96 s ⇒ ~0.78 s of playback: the sim's PUNCH_WINDUP (24 ticks ≈ 0.8 s) is timed to
// land the throw on that contact frame. Changing this scale means retiming cfg.actions.DUR.PUNCH_WINDUP to match.

/** One-shot playback speed by clip (loco clips scale with ground speed instead). D107's pack clips
 *  are already cut to a usable length, so they play at 1× until someone retimes them against
 *  cfg.actions.DUR — the punch note above is the worked example of what that entails. */
function oneShotScale(name: PlayerClipKind): number {
  if (name === 'kick') return KICK_TIME_SCALE;
  if (name === 'punch') return PUNCH_TIME_SCALE;
  if (name === 'slide') return SLIDE_TIME_SCALE;
  return 1;
}
const RUN_REFERENCE_SPEED = 3.4; // clip footfall matches ground speed at this m/s
const SPRINT_REFERENCE_SPEED = 10.0;
/** Model forward compensation — verified by a live toe-bone probe (D31): with offset π/2 the figure
 *  faced 90° counterclockwise off travel; ZERO puts the feet/visual forward on the sim facing. */
const MODEL_YAW_OFFSET = 0;

// ── back numbers (D37) ──
// A canvas-textured plane parented to the chest bone (Spine02) so the number rides every animation.
// Bone space is ≈ cm (the Meshy armature drives vertices in cm; the rig root normalizes to 1.8 m).
const BACK_NUM_SIZE_M = 0.3;
const BACK_NUM_UP_M = 0.04; // above the Spine02 origin — just below the shoulder blades (hair overhangs higher)
const BACK_NUM_BACK_M = 0.2; // straight out of the model-space back, clear of the chunky torso

/** Default squad numbering until M7 profiles/jerseys supply real ones: GK = 1, outfielders draw from a
 *  classic-kit set by entity id — stable per match, unique within a team (ids are contiguous, set len 10). */
export const JERSEY_NUMBERS = [7, 10, 4, 8, 11, 6, 9, 2, 5, 3] as const;
export function defaultBackNumber(e: { id: number; isGk: boolean }): number {
  return e.isGk ? 1 : JERSEY_NUMBERS[e.id % JERSEY_NUMBERS.length]!;
}

function numberTexture(num: number): THREE.CanvasTexture {
  const cv = document.createElement('canvas');
  cv.width = cv.height = 128;
  const g = cv.getContext('2d');
  if (g) {
    g.clearRect(0, 0, 128, 128);
    g.font = '900 92px system-ui, ui-sans-serif, sans-serif';
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.lineWidth = 12;
    g.lineJoin = 'round';
    g.strokeStyle = 'rgba(15, 20, 35, 0.9)';
    g.strokeText(String(num), 64, 70);
    g.fillStyle = '#ffffff';
    g.fillText(String(num), 64, 70);
  }
  const t = new THREE.CanvasTexture(cv);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

export interface PlayerRig {
  /** Positioned (feet at entity pos) and yawed by the caller, exactly like the pill mesh. */
  readonly object3d: THREE.Group;
  /** All skinned materials of this rig (cloned per rig) — telegraph tinting fans out over these. */
  readonly materials: THREE.MeshStandardMaterial[];
  /** Request the clip for this frame (rising edge retriggers one-shots; locomotion crossfades). */
  setClip(clip: PlayerClipKind, moveSpeed: number): void;
  /** D55: floor state — eases the theatrical lay-flat tumble in (fast slam) and out (slower rise). */
  setDown(down: boolean): void;
  /** Advance the mixer; dtSec is the render-frame delta (presentation clock). */
  update(dtSec: number): void;
}

class Rig implements PlayerRig {
  readonly object3d = new THREE.Group();
  readonly materials: THREE.MeshStandardMaterial[] = [];
  private readonly tilt = new THREE.Group(); // D55: the theatrical fall pivot (feet-level)
  private down01 = 0;
  private downTarget = 0;
  private readonly mixer: THREE.AnimationMixer;
  /** Bound lazily: tiers 2–3 land after rigs already exist, so actions are created on first use. */
  private readonly actions = new Map<PlayerClipKind, THREE.AnimationAction>();
  private readonly clips: ReadonlyMap<PlayerClipKind, THREE.AnimationClip>;
  /** The clip actually PLAYING (post-fallback), which is not always the one requested. */
  private active: PlayerClipKind = 'idle';
  private requested: PlayerClipKind = 'idle';
  private speed = 0;

  constructor(template: THREE.Object3D, clips: ReadonlyMap<PlayerClipKind, THREE.AnimationClip>, normScale: number, kit: KitPaint, heightScale: number, backNumber: number | null = null, private readonly seed = 0) {
    this.clips = clips;
    const inner = new THREE.Group();
    const model = cloneSkinned(template);
    model.traverse((o) => {
      if (o instanceof THREE.SkinnedMesh) {
        // D68: recolor stops at the chin so the face (eye-whites, teeth) is never repainted. The threshold
        // is a fraction of the mesh's BIND-POSE height (min→max local Y), matching the vertex `position.y`
        // the shader tests — scale-invariant (heightVar rides the group) and pose-invariant (bind pose).
        o.geometry.computeBoundingBox();
        const bb = o.geometry.boundingBox;
        const chinLocalY = bb ? bb.min.y + RECOLOR_CHIN_FRACTION * (bb.max.y - bb.min.y) : 1e9;
        const src = Array.isArray(o.material) ? o.material : [o.material];
        const cloned = src.map((m) => {
          const c = m.clone() as THREE.MeshStandardMaterial;
          c.color.setHex(0xffffff); // true texture; team identity rides the teal recolor shader (D34)
          // D36 grey-player fix: the Meshy export omits metallicFactor (glTF default = 1, fully metallic)
          // and relied on KHR_materials_specular/ior — dropped by the optimizer. Metal with no envMap
          // kills the diffuse texture entirely ⇒ grey. Force a matte cloth response.
          c.metalness = 0;
          c.roughness = 0.9;
          // D90/D91 raw-kit fix: the Meshy material re-exports the ALBEDO as its emissive texture — the
          // glTF emissive texture is literally the same `texture_0` object as the base colour, with
          // emissiveFactor = WHITE. MeshStandardMaterial ADDS `emissive × emissiveMap`, so the untouched
          // baked teal/white kit was painted back over the recolored diffuse.
          //   · D90: emissive WHITE ⇒ the raw kit at full strength. The REFEREE (never telegraphed) showed
          //     it outright ("bright blue"); outfielders only escaped because telegraph() rewrites their
          //     emissive to 0x000000 every frame.
          //   · D91: any NON-BLACK emissive re-introduced the STRIPE PATTERN, because the map modulated the
          //     glow — the controlled player (0x554400 marker) and every keeper (GK_GLOW) went stripy.
          // The map carries no intended detail (it IS the kit texture), and emissive is used purely as a
          // gameplay highlight (controlled marker / keeper glow / contact telegraph), so drop the map and
          // let emissive be the flat glow it is meant to be. Callers still set emissive explicitly.
          c.emissiveMap = null;
          c.emissive.setHex(0x000000);
          applyKitPaint(c, kit, chinLocalY);
          return c;
        });
        o.material = Array.isArray(o.material) ? cloned : cloned[0]!;
        this.materials.push(...cloned);
        o.frustumCulled = false; // skinned bounds don't track the pose; players are always near the ball cam
      }
    });
    inner.scale.setScalar(normScale * heightScale);
    inner.rotation.y = MODEL_YAW_OFFSET;
    inner.add(model);
    this.tilt.add(inner);
    this.object3d.add(this.tilt);

    // D37: back number — a decal plane on the (per-rig cloned) chest bone. NOT in `materials`, so the
    // telegraph/emissive tint fan-out never touches it.
    if (backNumber !== null) {
      const bone = model.getObjectByName('Spine02') ?? model.getObjectByName('Spine01') ?? model.getObjectByName('Spine');
      if (bone) {
        // The bone's local axes are twisted by the rig's joint orientations — don't guess them. Cancel
        // the bind-pose bone rotation instead, so the decal is placed/oriented in MODEL space (where
        // backward is unambiguously −Z, D31) while still riding the bone through every animation.
        this.object3d.updateMatrixWorld(true);
        const boneQ = bone.getWorldQuaternion(new THREE.Quaternion());
        const boneS = bone.getWorldScale(new THREE.Vector3());
        const invQ = boneQ.clone().invert();
        const plane = new THREE.Mesh(
          new THREE.PlaneGeometry(1, 1),
          new THREE.MeshBasicMaterial({ map: numberTexture(backNumber), transparent: true, depthWrite: false }),
        );
        plane.quaternion.copy(invQ).multiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI));
        plane.position.set(0, BACK_NUM_UP_M, -BACK_NUM_BACK_M).applyQuaternion(invQ).divide(boneS);
        plane.scale.setScalar(BACK_NUM_SIZE_M / boneS.x);
        bone.add(plane);
      }
    }

    this.mixer = new THREE.AnimationMixer(model);
    this.actionFor('idle').play(); // a real looping idle now (D107), not the run clip frozen at 0
  }

  /** The clip this rig can play for `clip` right now — see resolveAvailableClip. */
  private resolve(clip: PlayerClipKind): PlayerClipKind {
    return resolveAvailableClip(clip, (c) => this.clips.has(c));
  }

  /** Action for an ALREADY-RESOLVED clip, created on first use so late tiers bind for free. */
  private actionFor(clip: PlayerClipKind): THREE.AnimationAction {
    const existing = this.actions.get(clip);
    if (existing) return existing;
    const source = this.clips.get(clip);
    if (!source) throw new Error(`player_anims: missing clip '${clip}'`);
    const action = this.mixer.clipAction(source);
    if (ONE_SHOT_CLIPS.has(clip)) {
      action.setLoop(THREE.LoopOnce, 1);
      action.clampWhenFinished = true;
      action.timeScale = oneShotScale(clip);
    }
    this.actions.set(clip, action);
    return action;
  }

  setDown(down: boolean): void {
    // D107: `trip`/`fallen` already put the body on the floor, so D55's 90° theatrical tilt would
    // double up and bury the player. Suppress it for those — but keep it while the clip is still
    // the borrowed slide (tier 2 in flight), which is exactly what D55 was miming in the first place.
    this.downTarget = down && !DOWN_CLIPS.has(this.active) ? 1 : 0;
  }

  setClip(clip: PlayerClipKind, moveSpeed: number): void {
    this.speed = moveSpeed;
    if (clip === this.requested) {
      // Same request as last frame — but a deferred clip may have landed since, so upgrade a
      // looping fallback in place rather than waiting for the next state change.
      const resolved = this.resolve(clip);
      if (resolved !== this.active && !ONE_SHOT_CLIPS.has(resolved)) this.start(clip, FADE_LOCO_S);
      return;
    }
    const prev = this.requested;
    this.requested = clip;
    if (ONE_SHOT_CLIPS.has(clip)) {
      this.start(clip, FADE_ONESHOT_S); // rising edge — (re)trigger immediately
      return;
    }
    // D126: a DOWN clip may interrupt a running one-shot. `gk_dive` is 3.2 s of animation over a 16-tick
    // action, so the keeper was still mid-dive-crouch when the sim had already put him on the floor —
    // he crouched all the way through PRONE. A floor state is the ground truth about where the body is,
    // so it wins outright rather than queueing behind an animation that has outlived its action.
    if (clip === 'fallen') {
      this.start(clip, FADE_ONESHOT_OUT_S);
      return;
    }
    // locomotion request: if a one-shot is mid-flight let it finish (update() hands over), unless
    // the previous request was a one-shot that already ended and we never noticed
    const activeAction = this.actionFor(this.active);
    if (!ONE_SHOT_CLIPS.has(this.active) || !activeAction.isRunning()) {
      this.start(clip, ONE_SHOT_CLIPS.has(prev) ? FADE_ONESHOT_OUT_S : FADE_LOCO_S);
    }
  }

  private start(clip: PlayerClipKind, fadeS: number): void {
    const resolved = this.resolve(clip);
    if (this.active === resolved && !ONE_SHOT_CLIPS.has(resolved)) return;
    const from = this.actionFor(this.active);
    const to = this.actionFor(resolved);
    this.active = resolved;
    to.reset();
    if (ONE_SHOT_CLIPS.has(resolved)) {
      to.timeScale = oneShotScale(resolved);
    } else {
      // D109: looping clips start at a per-player phase and run slightly detuned. Without this a line
      // of players breathes in perfect unison — and three idle clips only turns that into three
      // synchronised groups. `reset()` zeroes time, so the offset has to be applied after it.
      to.timeScale = idleTimeScale(this.seed);
      to.time = idleStartOffset(this.seed, to.getClip().duration);
    }
    to.play();
    if (from !== to) {
      from.crossFadeTo(to, fadeS, false);
    }
  }

  update(dtSec: number): void {
    // D55: theatrical fall — slam down fast (~0.15 s, slight overshoot + a flail roll mid-flight),
    // rise slower on GETUP. Presentation-only; the sim owns the action state.
    if (this.down01 !== this.downTarget) {
      const rate = this.downTarget > this.down01 ? 7 : 2.6;
      const d = this.downTarget - this.down01;
      this.down01 += Math.sign(d) * Math.min(Math.abs(d), rate * dtSec);
    }
    const t = this.down01;
    this.tilt.rotation.x = -t * (Math.PI / 2) * 1.06 + Math.sin(t * Math.PI) * 0.18;
    this.tilt.rotation.z = Math.sin(t * Math.PI) * 0.55; // the flail
    this.tilt.position.y = Math.sin((t * Math.PI) / 2) * 0.1;
    // one-shot finished → fall back to whatever locomotion is currently requested
    if (ONE_SHOT_CLIPS.has(this.active) && !this.actionFor(this.active).isRunning() && !ONE_SHOT_CLIPS.has(this.requested)) {
      this.start(this.requested, FADE_ONESHOT_OUT_S);
    }
    if (this.active === 'run') {
      this.actionFor('run').timeScale = THREE.MathUtils.clamp(this.speed / RUN_REFERENCE_SPEED, 0.55, 2.2);
    } else if (this.active === 'sprint') {
      this.actionFor('sprint').timeScale = THREE.MathUtils.clamp(this.speed / SPRINT_REFERENCE_SPEED, 0.8, 1.5);
    }
    this.mixer.update(dtSec);
  }
}

export class AnimatedPlayerPool {
  private constructor(
    private readonly template: THREE.Object3D,
    /** Mutable on purpose: deferred tiers write into it and live rigs bind the new clips lazily. */
    private readonly clips: Map<PlayerClipKind, THREE.AnimationClip>,
    private readonly normScale: number,
  ) {}

  /**
   * Rendered height of the template, measured SKINNED (SkinnedMesh.computeBoundingBox bakes the
   * bind pose) — a naive Box3 sees only the 0.01-armature geometry (0.017 m) while the skeleton
   * drives vertices in cm space. Verified via the live browser probe (D21).
   */
  private static measureHeight(template: THREE.Object3D): number {
    template.updateMatrixWorld(true);
    const box = new THREE.Box3();
    const tmp = new THREE.Box3();
    template.traverse((o) => {
      if (o instanceof THREE.SkinnedMesh) {
        o.computeBoundingBox(); // skinned-aware, mesh-local
        tmp.copy(o.boundingBox).applyMatrix4(o.matrixWorld);
        box.union(tmp);
      }
    });
    const h = box.max.y - box.min.y;
    return h > 1e-6 ? h : MODEL_HEIGHT_M;
  }

  /**
   * Load the geometry carrier + TIER 1 only, and reject if any of those is missing/broken — they are
   * what a kickoff needs on screen. Tiers 2–3 stream in behind first render (D107): they are ~310 KB
   * and 10 requests that would otherwise sit in front of the thing this game sells, which is being
   * playable the instant the URL opens.
   */
  static async load(baseUrl = '/models/'): Promise<AnimatedPlayerPool> {
    const loader = new GLTFLoader();
    const tier1 = CLIP_TIERS[0];
    const [base, ...carriers] = await Promise.all([
      loader.loadAsync(`${baseUrl}player.glb`),
      ...tier1.map((name) => loader.loadAsync(`${baseUrl}anims_${name}.glb`)),
    ]);
    const clips = new Map<PlayerClipKind, THREE.AnimationClip>();
    tier1.forEach((name, i) => {
      const clip = carriers[i]?.animations[0];
      if (!clip) throw new Error(`${name}: no animation in carrier`);
      clips.set(name, clip);
    });
    const normScale = MODEL_HEIGHT_M / AnimatedPlayerPool.measureHeight(base.scene);
    const pool = new AnimatedPlayerPool(base.scene, clips, normScale);
    void pool.streamDeferredTiers(loader, baseUrl);
    return pool;
  }

  /**
   * Fetch the deferred tiers in the background, tier at a time so the outfield extras land before the
   * keeper set. Best-effort by design: a clip that fails to load leaves its fallback in place, which
   * is a slightly duller animation rather than a broken match.
   */
  private async streamDeferredTiers(loader: GLTFLoader, baseUrl: string): Promise<void> {
    for (const tier of CLIP_TIERS.slice(1)) {
      await Promise.all(
        tier.map(async (name) => {
          try {
            const gltf = await loader.loadAsync(`${baseUrl}anims_${name}.glb`);
            const clip = gltf.animations[0];
            if (clip) this.clips.set(name, clip);
          } catch {
            // keep the CLIP_FALLBACK substitute; nothing else to do at render time
          }
        }),
      );
    }
  }

  /** Which clips are resident right now — deferred tiers appear as they land. (Tests/diagnostics.) */
  loadedClips(): readonly PlayerClipKind[] {
    return [...this.clips.keys()];
  }

  /** A rig per entity: own skeleton + mixer, shared geometry, per-rig kit-recolored materials.
   *  `kit` repaints the two baked inks (see kitRecolor). `backNumber` renders on the jersey's back
   *  (null = none, e.g. the referee). */
  /** `seed` (the entity id) desynchronises this rig's looping clips from everyone else's (D109). */
  spawnRig(kit: KitPaint, heightScale = 1, backNumber: number | null = null, seed = 0): PlayerRig {
    return new Rig(this.template, this.clips, this.normScale, kit, heightScale, backNumber, seed);
  }
}
